import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getMe, apiErrText,
  staffComplianceQueue, staffComplianceConfirm, staffComplianceReturn, staffComplianceFileUrl,
  fileSizeText, COMPLIANCE_STATUS, COMPLIANCE_CADENCE, COMPLIANCE_CATEGORY, COMPLIANCE_POLICY_ONLY_LABEL, complianceReturnLine,
  type MeResponse, type ComplianceQueueRow, type ComplianceQueueView, type EffectiveComplianceStatus,
} from '../lib/api';
import { Skeleton } from '../components/ui/Skeleton';
import { EmptyState } from '../components/ui/EmptyState';
import { toast } from '../components/ui/Toast';

/**
 * 合规待审 / Compliance review queue — one org-wide list of every 履约档案 slot that needs a human
 * (Layer 1 step 4B, Eric 9/18). The package tab answers "what does THIS job still owe"; this page
 * answers "what is waiting on ME, across every job" — the thing that was missing when a 资质 could sit
 * uploaded-and-unreviewed for a week because nobody opened the package.
 *
 * 项目经理 (site_pm) cannot open /packages/:pid at all (the package browser carries pricing), so
 * `?mine=1` — rows on packages where they are the assigned PM — is their ONLY door into the 履约档案.
 * For them the page is read-only by construction: the server 403s every write, so no action button is
 * rendered. There are no money fields in this payload at all.
 */

/** The four sections, in the order 改改 works them. Each is a filter over one fetched ledger. */
const SECTIONS: Array<{ key: string; title: string; statuses: string[]; hint: string }> = [
  {
    key: 'review', title: '待审 / Awaiting review', statuses: ['uploaded'],
    hint: '劳务已上传，等我方确认。最早提交的排在前面。/ uploaded by the contractor — oldest submission first.',
  },
  {
    key: 'returned', title: '已退回待重传 / Returned — awaiting re-upload', statuses: ['returned'],
    hint: '已把理由发回劳务，等对方修正后重新上传。/ the reason went back to the contractor; waiting on a new upload.',
  },
  {
    key: 'expiring', title: '即将过期 / Expiring', statuses: ['expiring'],
    hint: '已确认但即将到期的证照 —— 过期后即视为缺失。/ confirmed but about to lapse — it reads as missing once it does.',
  },
  {
    key: 'overdue', title: '已过期 / 逾期失权 / Expired & rights lapsed', statuses: ['expired', 'lapsed'],
    hint: '过期或已过失权期限 —— 失权项过期后该项权利即丧失。/ expired, or past a rights-extinguishing bar.',
  },
];

const REVIEW_ROLES = ['gm', 'budget', 'procurement', 'commercial'];
const statusOf = (s: EffectiveComplianceStatus) => COMPLIANCE_STATUS[s] ?? { label: s, pill: 'pill-muted' };
const dateInput = (v: string | null | undefined) => (v ? v.slice(0, 10) : '');

export default function ComplianceQueue() {
  const qc = useQueryClient();
  const { data: me } = useQuery<MeResponse>({ queryKey: ['me'], queryFn: getMe });
  const role = me?.account?.role ?? '';
  const isPm = role === 'site_pm';
  const canReview = REVIEW_ROLES.includes(role);
  // ?mine=1 lives in the URL so 「只看我的」 is a shareable link, and it is auto-ON (and locked) for a
  // site_pm — the unscoped org queue is not their view, and their standards-workflow assignment is the
  // only scope the server answers for them.
  const [searchParams, setSearchParams] = useSearchParams();
  const mine = isPm || searchParams.get('mine') === '1';
  const setMine = (on: boolean) => setSearchParams((prev) => {
    if (on) prev.set('mine', '1'); else prev.delete('mine');
    return prev;
  }, { replace: true });
  const [category, setCategory] = useState('');

  const { data, isLoading, isError, error } = useQuery<ComplianceQueueView>({
    queryKey: ['compliance', 'queue', { mine, category }],
    queryFn: () => staffComplianceQueue({ category: category || undefined, mine }),   // default statuses = exactly the four sections; counts come back regardless
    enabled: me?.account?.subject_type === 'staff',
    refetchInterval: 60000,
  });

  if (!me?.authenticated) return <div className="card">请先登录 / please sign in.</div>;
  if (me.account?.subject_type !== 'staff') return <div className="card">此页仅内部员工可见 / internal staff only.</div>;

  const rows = data?.rows ?? [];
  const counts = data?.counts;

  return (
    <div>
      <h2 style={{ margin: '4px 0' }}>📁 合规待审 / Compliance review</h2>
      <p className="lede small" style={{ marginTop: 0 }}>
        所有在建合同的履约资料待办，集中在一页。点任意一行可打开该工程包的「履约档案」页签。
        / Every 履约档案 slot that needs a human, across all jobs. A row opens that package's compliance tab.
      </p>

      <div className="card">
        <div className="row-wrap small" style={{ gap: 12, alignItems: 'center' }}>
          {counts && (
            <>
              <span>待审 <strong>{counts.uploaded}</strong></span>
              <span>已退回 <strong>{counts.returned}</strong></span>
              <span>即将过期 <strong>{counts.expiring}</strong></span>
              <span>已过期 <strong>{counts.expired}</strong></span>
              <span>逾期失权 <strong>{counts.lapsed}</strong></span>
              <span className="muted">缺失 {counts.missing}</span>
            </>
          )}
          <span style={{ flex: 1 }} />
          <label className="row" style={{ gap: 4, alignItems: 'center', marginBottom: 0 }}>
            <span className="muted">类别 / category</span>
            <select value={category} onChange={(e) => setCategory(e.target.value)} style={{ width: 'auto' }}>
              <option value="">全部 / all</option>
              {Object.keys(COMPLIANCE_CATEGORY).map((c) => (
                <option key={c} value={c}>{COMPLIANCE_CATEGORY[c]}</option>
              ))}
            </select>
          </label>
          <label className="row" style={{ gap: 4, alignItems: 'center', marginBottom: 0 }}
            title="只看我担任项目经理的工程 / only jobs where I am the assigned 项目经理">
            <input type="checkbox" checked={mine} disabled={isPm} onChange={(e) => setMine(e.target.checked)} />
            <span>只看我的 / Mine only</span>
          </label>
        </div>
        {isPm && (
          <div className="muted small" style={{ marginTop: 6 }}>
            项目经理视图为只读，且不含任何金额。/ the 项目经理 view is read-only and carries no amounts.
          </div>
        )}
      </div>

      {isLoading && <div className="card"><Skeleton lines={5} /></div>}
      {isError && <div className="card">读取失败 / could not load: {apiErrText(error)}</div>}

      {data && rows.length === 0 && (
        <EmptyState title="暂无待办 / Nothing waiting"
          hint={mine
            ? '我负责的工程暂无待审履约资料。/ nothing outstanding on the jobs assigned to me.'
            : '所有合同的履约资料都已处理。/ every compliance slot is up to date.'} />
      )}

      {data && rows.length > 0 && SECTIONS.map((s) => {
        const sec = rows
          .filter((r) => s.statuses.includes(r.effective_status))
          // 待审: oldest submission first — the thing that has been waiting longest is the thing to do.
          .sort((a, b) => (a.submitted_at ?? a.created_at).localeCompare(b.submitted_at ?? b.created_at));
        if (sec.length === 0) return null;
        return (
          <div className="card" key={s.key}>
            <div className="row-wrap" style={{ alignItems: 'center', gap: 8 }}>
              <strong>{s.title}（{sec.length}）</strong>
            </div>
            <div className="muted small" style={{ marginBottom: 6 }}>{s.hint}</div>
            {/* .cmp-wrap for the scroll shell, but NOT .cmp itself: its `white-space: nowrap` is right
                for a price comparison and wrong here — eight columns of CJK would push 操作 off-screen. */}
            <div className="cmp-wrap">
              <table className="tbl-hover">
                <thead><tr>
                  <th style={{ minWidth: 170 }}>工程包 / Package</th>
                  <th>劳务公司 / Labor co</th>
                  <th style={{ minWidth: 180 }}>项目 / Item</th>
                  <th>频次 / Cadence</th>
                  <th>状态 / Status</th>
                  <th>文件 / Files</th>
                  <th>日期 / Dates</th>
                  <th style={{ minWidth: 150 }}>操作 / Actions</th>
                </tr></thead>
                <tbody>
                  {sec.map((r) => <QueueRow key={r.id} row={r} canReview={canReview}
                    onDone={() => { qc.invalidateQueries({ queryKey: ['compliance'] }); }} />)}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function QueueRow({ row, canReview, onDone }: { row: ComplianceQueueRow; canReview: boolean; onDone: () => void }) {
  const [returning, setReturning] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const st = statusOf(row.effective_status);

  async function act(fn: () => Promise<unknown>, msg: string) {
    setBusy(true);
    try { await fn(); toast(msg, { kind: 'success' }); setReturning(false); setReason(''); onDone(); }
    catch (e) { toast(apiErrText(e), { kind: 'error' }); }
    finally { setBusy(false); }
  }

  return (
    <>
      <tr>
        <td style={{ whiteSpace: 'normal' }}>
          {/* the drill-down: the same slot, in the full A–H context of its own contract */}
          {/* review 9/18: /packages/:pid is requireInternalNonPm — a site_pm's only door is this page, so no dead link */}
          {canReview
            ? <Link to={`/packages/${row.package_id}?tab=compliance`}>{row.package_name ?? row.package_id}</Link>
            : <span>{row.package_name ?? row.package_id}</span>}
          {row.project_name && <div className="muted small">{row.project_name}</div>}
        </td>
        <td className="small" style={{ whiteSpace: 'normal' }}>{row.labor_co_name ?? '—'}</td>
        <td style={{ whiteSpace: 'normal' }}>
          <div><strong>{row.item_code}</strong> {row.label_zh}</div>
          <div className="muted small">{row.label_en}</div>
          {!!row.policy_only && <span className="pill pill-muted small">{COMPLIANCE_POLICY_ONLY_LABEL}</span>}
          {!!row.rights_extinguishing && <span className="pill pill-bad small" style={{ marginLeft: 4 }}>失权条款 / rights-extinguishing</span>}
          {row.review_note_md && row.effective_status === 'returned' && (
            <div className="small" style={{ color: 'var(--bad)' }}>{complianceReturnLine(row.review_note_md)}</div>
          )}
        </td>
        <td className="small">
          {COMPLIANCE_CADENCE[row.cadence] ?? row.cadence}
          {row.period_key && <div className="muted">{row.period_key}</div>}
        </td>
        <td><span className={`pill ${st.pill} small`}>{st.label}</span></td>
        <td className="small" style={{ whiteSpace: 'normal' }}>
          {row.files.length === 0 ? <span className="muted">—</span> : row.files.map((f) => (
            <div key={f.id}>
              <a href={staffComplianceFileUrl(f.file_id)}>⬇ {f.file_name}</a>
              <span className="muted"> · {fileSizeText(f.size_bytes)}</span>
            </div>
          ))}
        </td>
        <td className="small">
          {row.submitted_at && <div className="muted">提交 / submitted {dateInput(row.submitted_at)}</div>}
          {row.expires_at && <div>有效期 / valid {dateInput(row.expires_at)}</div>}
          {row.hard_deadline_at && <div style={{ color: 'var(--bad)' }}>失权期限 / bar {dateInput(row.hard_deadline_at)}</div>}
        </td>
        <td className="small">
          {!canReview
            ? <span className="muted">只读 / read-only</span>
            : (
              <div className="row-wrap" style={{ gap: 4 }}>
                {row.status === 'uploaded' && (
                  <button className="btn-good small" disabled={busy}
                    onClick={() => act(() => staffComplianceConfirm(row.id), '已确认 / confirmed')}>✅ 确认</button>
                )}
                {(row.status === 'uploaded' || row.status === 'confirmed') && (
                  <button className="small" disabled={busy} onClick={() => setReturning((v) => !v)}>退回</button>
                )}
                <Link className="btn small" to={`/packages/${row.package_id}?tab=compliance`}>打开档案 / Open file</Link>
              </div>
            )}
        </td>
      </tr>
      {returning && (
        <tr>
          <td colSpan={8} style={{ background: 'var(--stripe)', whiteSpace: 'normal' }}>
            <div className="row-wrap" style={{ gap: 8, alignItems: 'flex-end' }}>
              <div className="field grow" style={{ marginBottom: 0 }}>
                <label>退回理由（必填，会原文发给劳务）/ reason (required — sent verbatim to the contractor)</label>
                <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="例如：保险单没有承保名单附页" />
              </div>
              <button className="btn-warn small" disabled={busy || !reason.trim()}
                onClick={() => act(() => staffComplianceReturn(row.id, reason.trim()), '已退回 / returned')}>确认退回 / Return</button>
              <button className="small" onClick={() => { setReturning(false); setReason(''); }}>取消 / Cancel</button>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
